/**
 * LLMSettings (VH-117, formerly PhotoAnalysisSettings / VH-71)
 *
 * Admin UI for configuring the LLM used per analyzer. Each analyzer can be set
 * to a PROVIDER (OpenAI / Anthropic / Gemini) and a specific MODEL from that
 * provider's curated list. The blood_test analyzer is a Claude text analyzer:
 * its provider is locked to anthropic — only the model is selectable.
 *
 * Both provider and model are stored server-side and take effect immediately
 * (no restart). The model shown is the REAL stored model (from `model_name`),
 * never a hardcoded label.
 *
 * HP-34: while HubPlatform holds the AI settings (`hub_managed`), the screen only
 * shows them and links to HubPlatform.
 */
import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { adminAPI } from '../../services/api'
import HubManagedNotice from './HubManagedNotice'

// Provider dropdown options. Labels come from i18n; no model strings here.
const PROVIDER_OPTIONS = ['openai', 'anthropic', 'gemini']

// Client mirror of the backend's curated model registry
// (admin_service.AVAILABLE_MODELS). Used ONLY as a fallback to populate the
// Model dropdown when a provider is switched locally to one that is not
// represented in the current API response — the API returns `available_models`
// only for each analyzer's PERSISTED provider. These are real model IDs (not
// display labels), so there are no stale-label concerns. Kept in sync with the
// backend registry; the server still validates model ∈ provider on save (422).
// VH-258: only a floor now. The server returns `available_models` from the provider
// itself, so this is used solely for a provider the current response does not cover.
const FALLBACK_PROVIDER_MODELS = {
  openai: ['gpt-5-mini', 'gpt-5'],
  anthropic: ['claude-opus-5', 'claude-sonnet-5'],
  gemini: ['gemini-2.5-pro'],
}

export default function LLMSettings() {
  const { t } = useTranslation()
  // Each entry: { analyzer_name, provider, model, available_models,
  //               provider_locked, api_key_configured }
  const [analyzers, setAnalyzers] = useState([])
  // provider -> [modelId]; built from the API's persisted rows, backed by the
  // fallback registry so every provider (incl. gemini) has options.
  const [providerModels, setProviderModels] = useState(FALLBACK_PROVIDER_MODELS)
  // provider -> bool; whether an API key is configured for that provider.
  const [providerKeyStatus, setProviderKeyStatus] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  // HP-34: { managed, site } — read-only while HubPlatform holds the settings.
  const [hub, setHub] = useState({ managed: false, site: null })

  // useCallback so the mount effect below can declare it as a dependency
  // without refetching on every render. It deliberately captures nothing
  // unstable — setState setters are stable, and the error is stored as an
  // i18n *key* (translated at render) rather than calling t() in here, which
  // would tie this callback's identity to t and re-fire the fetch whenever t
  // changed identity.
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await adminAPI.getPhotoAnalysisSettings()
      const data = response.data

      const rows = (data.analyzers || []).map((a) => ({
        analyzer_name: a.analyzer_name,
        provider: a.provider,
        // Default-select the REAL stored model.
        model: a.model_name,
        available_models: a.available_models || [],
        provider_locked: !!a.provider_locked,
        api_key_configured: !!a.api_key_configured,
      }))

      // Build the provider -> models map from persisted rows (available_models
      // reflect each row's persisted provider), overlaying the fallback so
      // providers not currently in use still have options.
      const pm = { ...FALLBACK_PROVIDER_MODELS }
      const ks = {}
      rows.forEach((r) => {
        if (r.provider && r.available_models.length) pm[r.provider] = r.available_models
        if (r.provider) ks[r.provider] = r.api_key_configured
      })

      setProviderModels(pm)
      setProviderKeyStatus(ks)
      setAnalyzers(rows)
      setHub({ managed: !!data.hub_managed, site: data.hub_site ?? null })
    } catch (err) {
      console.error('Failed to fetch LLM settings:', err)
      setError('common.error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  const modelsForProvider = (provider) => providerModels[provider] || []

  const handleProviderChange = (name, provider) => {
    setAnalyzers((prev) =>
      prev.map((a) => {
        if (a.analyzer_name !== name) return a
        // Provider changed: keep the model if still valid for the new provider,
        // otherwise reset to the first available model so the payload stays
        // valid (backend rejects a model that doesn't belong to the provider).
        const models = modelsForProvider(provider)
        const model = models.includes(a.model) ? a.model : (models[0] || '')
        return { ...a, provider, model }
      })
    )
  }

  const handleModelChange = (name, model) => {
    setAnalyzers((prev) =>
      prev.map((a) => (a.analyzer_name === name ? { ...a, model } : a))
    )
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      const payload = analyzers.map((a) => ({
        analyzer_name: a.analyzer_name,
        provider: a.provider,
        model: a.model,
      }))
      await adminAPI.updatePhotoAnalysisSettings({ analyzers: payload })
      toast.success(t('admin.llmSettings.saved', 'LLM settings saved'))
      // Refetch so displayed state (models, key status) reflects what persisted.
      await fetchSettings()
    } catch (err) {
      console.error('Failed to save LLM settings:', err)
      toast.error(t('admin.llmSettings.saveFailed', 'Failed to save settings'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[...Array(7)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-200 rounded" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        {/* `error` holds an i18n key — see fetchSettings. */}
        <p className="text-sm text-red-700">{t(error)}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">
          {t('admin.llmSettings.title', 'LLM Settings')}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {hub.managed
            ? t('admin.llmSettings.descriptionHub', 'The AI provider and model each analyzer uses.')
            : t(
              'admin.llmSettings.description',
              'Configure the AI provider and model used for each analyzer. Changes take effect immediately.'
            )}
        </p>
        {hub.managed && <HubManagedNotice site={hub.site} />}
      </div>

      {/* Analyzer table */}
      <div className="overflow-hidden border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.llmSettings.analyzer', 'Analyzer')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.llmSettings.provider', 'Provider')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.llmSettings.model', 'Model')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.llmSettings.apiKeyStatus', 'API Key')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface-container divide-y divide-gray-200">
            {analyzers.map((a) => {
              const name = a.analyzer_name
              const keyOk = providerKeyStatus[a.provider]
              const models = modelsForProvider(a.provider)
              return (
                <tr key={name}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {t(`admin.llmSettings.analyzers.${name}`, name)}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={a.provider}
                      disabled={a.provider_locked || hub.managed}
                      onChange={(e) => handleProviderChange(name, e.target.value)}
                      aria-label={t('admin.llmSettings.provider', 'Provider')}
                      className="block w-full focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                    >
                      {PROVIDER_OPTIONS.map((value) => (
                        <option key={value} value={value}>
                          {t(`admin.llmSettings.providers.${value}`, value)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={a.model}
                      disabled={hub.managed}
                      onChange={(e) => handleModelChange(name, e.target.value)}
                      aria-label={t('admin.llmSettings.model', 'Model')}
                      className="block w-full focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                    >
                      {/* Ensure the currently-stored model is always selectable,
                          even if it's outside the curated list (e.g. legacy). */}
                      {!models.includes(a.model) && a.model && (
                        <option value={a.model}>{a.model}</option>
                      )}
                      {models.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    {keyOk ? (
                      <CheckCircleIcon
                        className="h-5 w-5 text-green-500"
                        title={t('admin.llmSettings.keyConfigured', 'API key configured')}
                      />
                    ) : (
                      <ExclamationTriangleIcon
                        className="h-5 w-5 text-red-500"
                        title={t('admin.llmSettings.keyMissing', 'API key missing')}
                      />
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Save button — not while HubPlatform holds the settings (HP-34) */}
      {!hub.managed && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-on-primary bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          </button>
        </div>
      )}
    </div>
  )
}
